import { useState } from 'react'
import { ChevronDown, ChevronUp, Copy, CheckCircle, Search, Globe } from 'lucide-react'

interface SEOPreviewProps {
  metaTitle?: string | null
  metaDescription?: string | null
  slug?: string | null
}

export function SEOPreview({ metaTitle, metaDescription, slug }: SEOPreviewProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  // Don't render if no SEO data
  if (!metaTitle && !metaDescription && !slug) return null

  const copyField = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">SEO Preview</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-slate-100">
          {/* Google SERP Preview */}
          <div className="mt-3 bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs text-slate-400 mb-2 uppercase tracking-wider font-medium">
              Vista previa en Google
            </p>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Globe className="w-3 h-3" />
                <span>strtgy.com{slug ? `/${slug}` : '/blog/...'}</span>
              </div>
              <h3 className="text-lg text-blue-700 hover:underline cursor-default leading-tight">
                {metaTitle || 'Título del artículo'}
              </h3>
              <p className="text-sm text-slate-600 leading-snug line-clamp-2">
                {metaDescription || 'Descripción del artículo aparecerá aquí...'}
              </p>
            </div>
          </div>

          {/* Individual fields with copy */}
          <div className="space-y-3">
            {metaTitle && (
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <label className="text-xs text-slate-500 block mb-0.5">Meta Title</label>
                  <p className="text-sm text-slate-700 truncate">{metaTitle}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {metaTitle.length}/60 caracteres
                  </p>
                </div>
                <button
                  onClick={() => copyField(metaTitle, 'title')}
                  className="text-slate-400 hover:text-slate-600 p-1"
                >
                  {copiedField === 'title' ? (
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            )}

            {metaDescription && (
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <label className="text-xs text-slate-500 block mb-0.5">Meta Description</label>
                  <p className="text-sm text-slate-700">{metaDescription}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {metaDescription.length}/160 caracteres
                  </p>
                </div>
                <button
                  onClick={() => copyField(metaDescription, 'description')}
                  className="text-slate-400 hover:text-slate-600 p-1"
                >
                  {copiedField === 'description' ? (
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            )}

            {slug && (
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <label className="text-xs text-slate-500 block mb-0.5">Slug</label>
                  <p className="text-sm text-slate-700 font-mono">{slug}</p>
                </div>
                <button
                  onClick={() => copyField(slug, 'slug')}
                  className="text-slate-400 hover:text-slate-600 p-1"
                >
                  {copiedField === 'slug' ? (
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
